<script setup>
import { computed, onMounted, ref } from 'vue';
import { useStore } from 'dashboard/composables/store.js';
import { useAccount } from 'dashboard/composables/useAccount';
import { format } from 'date-fns';

import BillingCard from './components/BillingCard.vue';
import BaseSettingsHeader from '../components/BaseSettingsHeader.vue';
import SettingsLayout from '../SettingsLayout.vue';
import ButtonV4 from 'next/button/Button.vue';

const { currentAccount } = useAccount();
const store = useStore();

const isLoading = ref(false);
const isCheckoutLoading = ref(false);
const plans = ref([]);
const addons = ref([]);
const subscription = ref(null);

const fetchBillingDetails = async () => {
  isLoading.value = true;
  try {
    const plansResponse = await store.dispatch('accounts/getBillingPlans');
    plans.value = plansResponse || [];
    
    const addonsResponse = await store.dispatch('accounts/getAddons');
    addons.value = addonsResponse || [];
    
    const subResponse = await store.dispatch('accounts/getBillingSubscription');
    subscription.value = subResponse || null;
  } catch (error) {
    console.error(error);
  } finally {
    isLoading.value = false;
  }
};

const handleCheckout = async (planId) => {
  isCheckoutLoading.value = true;
  try {
    await store.dispatch('accounts/checkoutBilling', planId);
  } catch (error) {
    isCheckoutLoading.value = false;
  }
};

const handleCheckoutAddon = async (addonPlanId) => {
  isCheckoutLoading.value = true;
  try {
    await store.dispatch('accounts/checkoutAddon', addonPlanId);
  } catch (error) {
    isCheckoutLoading.value = false;
  }
};

const handlePortal = async () => {
  isCheckoutLoading.value = true;
  try {
    await store.dispatch('accounts/billingPortal');
  } catch (error) {
    isCheckoutLoading.value = false;
  }
};

const currentPlanId = computed(() => {
  return subscription.value?.subscription_plan?.id;
});

const currentPlanName = computed(() => {
  return subscription.value?.subscription_plan?.name || 'Demo de Prueba';
});

const currentStatus = computed(() => {
  return subscription.value?.status || currentAccount.value.status;
});

const isDemoPlan = (plan) => {
  return plan.name.toLowerCase().includes('demo') || parseFloat(plan.price_monthly || 0) === 0;
};

const isPlanDisabled = (plan) => {
  if (currentPlanId.value === plan.id) return true;
  if (isCheckoutLoading.value) return true;
  // If it's the demo plan and they are not currently on it, they cannot select it
  if (isDemoPlan(plan) && currentPlanId.value !== plan.id) return true;
  return false;
};

const trialEndsAt = computed(() => {
  if (!subscription.value?.trial_ends_at) return null;
  return format(new Date(subscription.value.trial_ends_at), 'dd MMM, yyyy');
});

onMounted(() => {
  fetchBillingDetails();
});
</script>

<template>
  <SettingsLayout
    :is-loading="isLoading"
    :loading-message="$t('ATTRIBUTES_MGMT.LOADING')"
  >
    <template #header>
      <BaseSettingsHeader
        title="Facturación y Planes"
        description="Administra tu plan de suscripción, mejora tus límites y mantén tu cuenta activa."
        feature-name="billing"
      />
    </template>
    <template #body>
      <section class="grid gap-4">
        
        <!-- Estado Actual -->
        <BillingCard
          title="Estado Actual de tu Cuenta"
          description="Resumen de tu suscripción actual y tiempo de prueba."
        >
          <div class="grid lg:grid-cols-3 sm:grid-cols-2 grid-cols-1 gap-2 divide-x divide-n-weak">
            <div class="px-4">
              <span class="block text-sm text-slate-500">Plan Actual</span>
              <span class="block text-lg font-medium text-slate-900 dark:text-slate-100">{{ currentPlanName }}</span>
            </div>
            <div class="px-4">
              <span class="block text-sm text-slate-500">Estado</span>
              <span class="block text-lg font-medium capitalize" :class="currentStatus === 'active' ? 'text-green-600' : 'text-red-600'">
                {{ currentStatus }}
              </span>
            </div>
            <div class="px-4" v-if="trialEndsAt && !currentPlanId">
              <span class="block text-sm text-slate-500">Fin del Demo</span>
              <span class="block text-lg font-medium text-slate-900 dark:text-slate-100">{{ trialEndsAt }}</span>
            </div>
          </div>
          <div class="mt-4 pt-4 border-t border-slate-100 dark:border-slate-800 flex justify-end" v-if="currentPlanId">
            <ButtonV4
              class="w-full sm:w-auto"
              @click="handlePortal"
              :is-loading="isCheckoutLoading"
              :disabled="isCheckoutLoading"
              blue
            >
              Gestionar Facturación y Pagos
            </ButtonV4>
          </div>
        </BillingCard>

        <!-- Planes Disponibles -->
        <h3 class="text-xl font-medium mt-6 mb-2 text-slate-900 dark:text-white">Planes Disponibles</h3>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="plan in plans" :key="plan.id" class="flex flex-col border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-white dark:bg-slate-800 shadow-sm relative">
            
            <div v-if="currentPlanId === plan.id" class="absolute top-0 right-0 bg-green-500 text-white text-xs px-2 py-1 rounded-bl-lg rounded-tr-lg font-medium">
              Plan Actual
            </div>

            <h4 class="text-xl font-bold text-slate-900 dark:text-white mb-2">{{ plan.name }}</h4>
            <div class="mb-4">
              <span class="text-3xl font-bold text-slate-900 dark:text-white">${{ parseFloat(plan.price_monthly || 0).toFixed(2) }} USD</span>
              <span class="text-slate-500 dark:text-slate-400">/mes</span>
            </div>
            
            <ul class="flex-grow space-y-3 mb-6">
              <li v-for="(feature, index) in plan.features || []" :key="index" class="flex items-center text-slate-600 dark:text-slate-300">
                <span class="i-lucide-check text-green-500 mr-2 text-lg"></span>
                <span>{{ feature }}</span>
              </li>
              <li v-if="currentPlanId === plan.id && subscription?.whatsapp_campaign_messages_available" class="flex items-center text-slate-600 dark:text-slate-300 font-bold">
                <span class="i-lucide-check text-green-500 mr-2 text-lg"></span>
                <span>{{ subscription.whatsapp_campaign_messages_available }} Mensajes disponibles en total</span>
              </li>
              <li v-if="!plan.features || plan.features.length === 0" class="text-slate-500 italic text-sm">
                Sin características configuradas.
              </li>
            </ul>

            <ButtonV4 
              class="w-full justify-center" 
              :solid="currentPlanId !== plan.id" 
              :slate="currentPlanId === plan.id"
              :blue="currentPlanId !== plan.id"
              :disabled="isPlanDisabled(plan)"
              :is-loading="isCheckoutLoading"
              @click="handleCheckout(plan.id)"
            >
              {{ currentPlanId === plan.id ? 'Seleccionado' : (isDemoPlan(plan) ? 'No Disponible' : 'Pagar este Plan') }}
            </ButtonV4>
          </div>
          
          <div v-if="plans.length === 0" class="col-span-full text-center py-8 text-slate-500">
            No hay planes de suscripción configurados en este momento.
          </div>
        </div>

        <!-- Paquetes Adicionales -->
        <h3 class="text-xl font-medium mt-8 mb-2 text-slate-900 dark:text-white">Paquetes Adicionales</h3>
        <p class="text-sm text-slate-600 mb-4">Adquiere paquetes de mensajes adicionales para tus campañas de WhatsApp.</p>
        
        <div class="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
          <div v-for="addon in addons" :key="addon.id" class="flex flex-col border border-slate-200 dark:border-slate-700 rounded-lg p-6 bg-white dark:bg-slate-800 shadow-sm relative">
            <h4 class="text-xl font-bold text-slate-900 dark:text-white mb-2">{{ addon.name }}</h4>
            <div class="mb-4">
              <span class="text-3xl font-bold text-slate-900 dark:text-white">${{ parseFloat(addon.price).toFixed(2) }} USD</span>
            </div>
            
            <ul class="flex-grow space-y-3 mb-6">
              <li class="flex items-center text-slate-600 dark:text-slate-300">
                <span class="i-lucide-check text-green-500 mr-2 text-lg"></span>
                <span>{{ addon.quantity.toLocaleString() }} Mensajes adicionales</span>
              </li>
              <li class="flex items-center text-slate-600 dark:text-slate-300">
                <span class="i-lucide-check text-green-500 mr-2 text-lg"></span>
                <span>No expiran al final del mes</span>
              </li>
            </ul>

            <ButtonV4 
              class="w-full justify-center" 
              solid blue
              :is-loading="isCheckoutLoading"
              :disabled="isCheckoutLoading"
              @click="handleCheckoutAddon(addon.id)"
            >
              Comprar Paquete
            </ButtonV4>
          </div>
          
          <div v-if="addons.length === 0" class="col-span-full text-center py-8 text-slate-500">
            No hay paquetes adicionales configurados en este momento.
          </div>
        </div>

      </section>
    </template>
  </SettingsLayout>
</template>
